import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { BRAND } from "../../data/content";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const from = location.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/staff");
      navigate(from, { replace: true });
      toast.success(`Welcome back, ${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center relative overflow-hidden" data-testid="page-admin-login">
      <div className="absolute inset-0 opacity-30">
        <img src="https://images.unsplash.com/photo-1762279389006-43963a0cee55?w=1280&q=75&auto=format&fit=crop" alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1C] via-[#0A0F1C]/95 to-[#0A0F1C]"></div>

      <div className="relative w-full max-w-md mx-6">
        <Link to="/" className="flex justify-center mb-10" data-testid="login-logo">
          <div className="bg-white px-6 py-4 rounded-sm">
            <img src={BRAND.logo} alt="Oxford Environmental Consultants" className="h-12 w-auto" />
          </div>
        </Link>
        <div className="border border-white/10 bg-white/[0.03] backdrop-blur-sm p-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure staff area
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-8">Sign in</h1>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-2 block">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#3FBAB0]"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-2 block">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#3FBAB0]"
                data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading} className="gs-gradient text-white font-bold px-6 py-3.5 w-full flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 transition-opacity" data-testid="login-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <p className="text-xs text-white/40 text-center mt-8">Access is managed by Oxford Environmental Consultants administrators. No public registration.</p>
        </div>
      </div>
    </div>
  );
}
